import { useState } from 'react';
import { Menu, MenuItem } from '@mui/material';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { createInvestorEditRoute } from 'adminConstants/routes';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';
import { AccountType } from 'further-types/investor';
import { useInvestor } from 'hooks/data/investor/useInvestor';
import downloadFile from 'utils/downloadFile';
import { useGetRole } from 'hooks/ui/useGetRole';
import LaunchPortalButton from './LaunchPortalButton';
import { ArrowDropDown } from '@mui/icons-material';
import IndividualDetails from './IndividualDetails';
import LegalEntityDetails from './LegalEntityDetails';
import Spacing from 'components/Spacing';
import PageHeader from 'components/PageHeader';
import Heading from 'components/Heading';
import ResponsiveActions from 'components/PageHeader/ResponsiveActions';
import Button from 'components/Button';
import useTaxReportDownload from 'hooks/useTaxReportDownload';
import DownloadStatementMenu from 'components/DownloadStatementMenu';
import { formatTaxYear } from 'further-ui/utils';
import { ApiResponses } from 'lib/trpcClient';

type Investor = ApiResponses['investor']['getInvestor'];

type Props = {
  investorDetail: Investor;
  id: string;
};

const InvestorDetails: React.FC<Props> = ({ investorDetail, id }) => {
  const [canEdit, canImpersonate] = useHasPermissions([
    RolePermission.EditInvestor,
    RolePermission.UseImpersonationAccessInvestor,
  ]);
  const { isSuperAdmin } = useGetRole();

  const onExportInvestorStatement = (data, valuationDate) => {
    const dateSuffix = valuationDate
      ? format(new Date(valuationDate), 'dd MM yyyy')
      : format(new Date(), 'dd MM yyyy');

    downloadFile(
      data,
      `${investorDetail?.fullName} - ${dateSuffix} Investor Statement.xlsx`,
    );
  };

  const { downloadReport, isDownloading } = useTaxReportDownload();

  const { exportInvestorStatement, isExportInvestorStatementLoading } =
    useInvestor(id, investorDetail?.firmId, {
      onExportInvestorStatement,
    });

  const [taxReportMenuAnchorEl, setTaxReportMenuAnchorEl] =
    useState<HTMLElement | null>(null);

  const hasAvailableTaxYears = investorDetail?.availableTaxYears?.length > 0;

  return (
    <Spacing>
      <PageHeader>
        <Heading variant="h2" noMargin>
          Investor details
        </Heading>
        <ResponsiveActions>
          {canEdit && (
            <Link to={createInvestorEditRoute(id)}>
              <Button
                fullWidth
                size="small"
                variant="contained"
                color="primary"
              >
                Edit investor
              </Button>
            </Link>
          )}
          {!!investorDetail?.investmentsCount && (
            <>
              <DownloadStatementMenu
                title="Download investor statement"
                hideDescription
                loading={isExportInvestorStatementLoading}
                onDownloadCurrentValuation={() =>
                  exportInvestorStatement(undefined)
                }
                onDownloadEarlierValuation={exportInvestorStatement}
              />

              {hasAvailableTaxYears && (
                <>
                  <Button
                    aria-controls="tax-report-menu"
                    aria-haspopup="true"
                    color="primary"
                    variant="contained"
                    size="small"
                    loading={isDownloading}
                    endIcon={<ArrowDropDown />}
                    onClick={(event) =>
                      setTaxReportMenuAnchorEl(event.currentTarget)
                    }
                  >
                    Download tax report
                  </Button>
                  <Menu
                    id="tax-report-menu"
                    anchorEl={taxReportMenuAnchorEl}
                    keepMounted
                    open={Boolean(taxReportMenuAnchorEl)}
                    onClose={() => setTaxReportMenuAnchorEl(null)}
                  >
                    {investorDetail.availableTaxYears.map((taxYear) => (
                      <MenuItem
                        key={`${taxYear.start}-${taxYear.end}`}
                        onClick={() => {
                          downloadReport(taxYear.start, id);
                          setTaxReportMenuAnchorEl(null);
                        }}
                      >
                        {formatTaxYear(taxYear.start, taxYear.end)}
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )}
            </>
          )}
          {canImpersonate && (
            <LaunchPortalButton
              investorId={id}
              firmDomain={investorDetail?.firm?.domain ?? ''}
            />
          )}
        </ResponsiveActions>
      </PageHeader>
      {investorDetail.accountType === AccountType.LegalEntity ? (
        <LegalEntityDetails
          investorDetail={investorDetail}
          showFirmName={isSuperAdmin}
          canEdit={canEdit}
        />
      ) : (
        <IndividualDetails
          investorDetail={investorDetail}
          showFirmName={isSuperAdmin}
          canEdit={canEdit}
        />
      )}
    </Spacing>
  );
};

export default InvestorDetails;
